'use client';
import React, { useState } from 'react';
import { Heart, DollarSign, Users, Briefcase, Leaf, Star } from 'lucide-react';

const categories = [
  {
    id: 'health',
    label: 'Health & Healing',
    icon: Leaf,
    description: 'Miracles of physical, mental & spiritual restoration',
    color: '#22c55e',
    bgColor: 'rgba(34,197,94,0.08)',
    borderColor: 'rgba(34,197,94,0.3)',
  },
  {
    id: 'love',
    label: 'Love & Relationships',
    icon: Heart,
    description: 'Miracles of connection, romance & deep belonging',
    color: '#ec4899',
    bgColor: 'rgba(236,72,153,0.08)',
    borderColor: 'rgba(236,72,153,0.3)',
  },
  {
    id: 'finances',
    label: 'Finances & Abundance',
    icon: DollarSign,
    description: 'Miracles of prosperity, wealth & financial freedom',
    color: '#c9a84c',
    bgColor: 'rgba(201,168,76,0.08)',
    borderColor: 'rgba(201,168,76,0.3)',
  },
  {
    id: 'family',
    label: 'Family & Home',
    icon: Users,
    description: 'Miracles of harmony, protection & family bonds',
    color: '#60a5fa',
    bgColor: 'rgba(96,165,250,0.08)',
    borderColor: 'rgba(96,165,250,0.3)',
  },
  {
    id: 'career',
    label: 'Career & Purpose',
    icon: Briefcase,
    description: 'Miracles of direction, success & fulfilling work',
    color: '#a78bfa',
    bgColor: 'rgba(167,139,250,0.08)',
    borderColor: 'rgba(167,139,250,0.3)',
  },
  {
    id: 'peace',
    label: 'Peace & Clarity',
    icon: Star,
    description: 'Miracles of stillness, wisdom & inner light',
    color: '#f0abfc',
    bgColor: 'rgba(240,171,252,0.08)',
    borderColor: 'rgba(240,171,252,0.3)',
  },
];

interface CategorySelectorProps {
  onSelect?: (categoryId: string) => void;
  selected?: string;
}

export default function CategorySelector({ onSelect, selected }: CategorySelectorProps) {
  const [localSelected, setLocalSelected] = useState<string>(selected || '');

  const handleSelect = (id: string) => {
    setLocalSelected(id);
    onSelect?.(id);
  };

  return (
    <section className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-12">
      <div className="text-center mb-10">
        <h2 className="font-display text-3xl md:text-4xl font-semibold gold-gradient-text mb-3">
          Choose Your Miracle Category
        </h2>
        <p className="text-muted-foreground max-w-lg mx-auto">
          Your miracle will be drawn from the sacred pool of messages in your chosen category.
          Each message is unique and meant only for you.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
        {categories.map((cat) => {
          const isSelected = localSelected === cat.id;
          return (
            <button
              key={`cat-${cat.id}`}
              onClick={() => handleSelect(cat.id)}
              className={`relative flex flex-col items-center text-center p-6 rounded-xl border transition-all duration-300 cursor-pointer group ${
                isSelected ? 'category-card-selected' : 'border-border hover:border-primary/30'
              }`}
              style={isSelected ? {} : { background: 'rgba(12,12,22,0.8)' }}
            >
              {isSelected && (
                <div className="absolute top-3 right-3 w-5 h-5 rounded-full flex items-center justify-center"
                  style={{ background: cat.color }}>
                  <span className="text-black text-xs font-bold">✓</span>
                </div>
              )}
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center mb-4 transition-all duration-300"
                style={{
                  background: isSelected ? cat.bgColor : 'rgba(30,30,48,0.8)',
                  border: `1px solid ${isSelected ? cat.color : 'rgba(255,255,255,0.08)'}`,
                  boxShadow: isSelected ? `0 0 20px ${cat.color}40` : 'none',
                }}
              >
                <cat.icon size={24} style={{ color: cat.color }} />
              </div>
              <h3 className="font-display text-sm font-semibold mb-2"
                style={{ color: isSelected ? cat.color : 'var(--foreground)' }}>
                {cat.label}
              </h3>
              <p className="text-xs text-muted-foreground leading-relaxed">{cat.description}</p>
            </button>
          );
        })}
      </div>

      {localSelected && (
        <p className="text-center mt-6 text-sm text-primary animate-fade-in">
          ✦ {categories.find(c => c.id === localSelected)?.label} selected — scroll down to claim your block
        </p>
      )}
    </section>
  );
}